import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { onStorageTrouble } from '@/store/localStore';
import { measureStorage, type StorageReport, type StorageTrouble } from '@/store/quota';
import { useSession } from '@/transport/SessionProvider';
import type { ConnectionPhase } from '@/transport/workerProtocol';
import { STATUS_DEBOUNCE_MS, type DisplayStatus } from '@/worker/connectionController';

/**
 * The single shared selector CR-01 §4 requires.
 *
 * Both the inbox and the open conversation read status from here. A component
 * that inferred status from its own last request would disagree with the one
 * beside it, and two rows showing different things is worse than one row showing
 * the wrong thing.
 *
 * The status reported is *this account's connection to our service*. It is not
 * another person's online state, not typing, not a receipt.
 */

export interface ConnectionStatusValue {
  /** §4: the debounced label, evaluated from the table top to bottom. */
  status: DisplayStatus;
  /** Locally committed messages the server has not acknowledged. */
  pendingCount: number;
  /** §8: shown as a detail while authenticated and otherwise ready. */
  slow: boolean;
  /** §4: channel enumeration, reported inside the inbox rather than as status. */
  refreshingChannels: boolean;
  retryNow: () => void;
  storage: StorageReport;
}

const ConnectionStatusContext = createContext<ConnectionStatusValue | null>(null);

/**
 * Maps the worker's phase onto the §4 table.
 *
 * The network hint is only ever used to choose between Waiting for network and
 * Connecting when there is no responsive connection, which is exactly what the
 * table says. It is never allowed to contradict a working connection: §2 and §10
 * are explicit that `navigator.onLine` is a heuristic, and a captive portal
 * reports online while our service is unreachable.
 */
function statusFor(phase: ConnectionPhase, networkAvailable: boolean): DisplayStatus {
  switch (phase) {
    case 'unauthenticated':
      return 'SIGN_IN';
    case 'ready':
      return 'CONNECTED';
    case 'syncing':
      return 'UPDATING';
    case 'connecting':
    case 'authenticating':
      return networkAvailable ? 'CONNECTING' : 'WAITING_FOR_NETWORK';
    case 'offline':
    case 'idle':
    default:
      return networkAvailable ? 'CONNECTING' : 'WAITING_FOR_NETWORK';
  }
}

export function ConnectionStatusProvider({ children }: { children: ReactNode }) {
  const { phase, retryNow } = useSession();

  /**
   * A hint, not an authority. It is read once and then only on the browser's own
   * events, and it feeds one branch of the table above.
   */
  const [networkAvailable, setNetworkAvailable] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const online = () => setNetworkAvailable(true);
    const offline = () => setNetworkAvailable(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  const target = statusFor(phase, networkAvailable);

  /**
   * §4: apply internally at once, debounce the visible transition by 750 ms, and
   * let a superseding transition replace a pending one. The three
   * action-required states bypass the debounce, because they need somebody to act
   * and delaying them only answers later.
   */
  const [status, setStatus] = useState<DisplayStatus>(target);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (target === status) return;

    const immediate =
      target === 'SIGN_IN' || target === 'UPDATE_CLIENT' || target === 'CERTIFICATE_FAILURE';
    if (immediate) {
      setStatus(target);
      return;
    }

    // Replacing any pending transition is what stops a flicker from being shown:
    // a reconnect faster than the debounce never reaches the screen at all.
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus(target), STATUS_DEBOUNCE_MS);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, [target, status]);

  const [storage, setStorage] = useState<StorageReport>({
    capability: 'full',
    usedBytes: null,
    quotaBytes: null,
    quotaExceeded: false,
    persisted: false,
  });

  useEffect(() => {
    let cancelled = false;

    const refresh = (trouble: StorageTrouble) => {
      void measureStorage(trouble).then((report) => {
        if (!cancelled) {
          setStorage(report);
        }
      });
    };

    // Re-measured whenever a write actually fails for space, so the reduced
    // capability notice appears at the moment it becomes true rather than at
    // the next poll.
    const unsubscribe = onStorageTrouble(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const value = useMemo<ConnectionStatusValue>(
    () => ({
      status,
      // Wired as the worker reports them; zero is the honest default rather than
      // a fabricated number.
      pendingCount: 0,
      slow: false,
      refreshingChannels: false,
      retryNow,
      storage,
    }),
    [status, retryNow, storage],
  );

  return (
    <ConnectionStatusContext.Provider value={value}>{children}</ConnectionStatusContext.Provider>
  );
}

export function useConnectionStatus(): ConnectionStatusValue {
  const value = useContext(ConnectionStatusContext);
  if (!value) {
    throw new Error('useConnectionStatus must be used inside ConnectionStatusProvider');
  }
  return value;
}
